import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import apiClient from '../api/client';
import ProductCard from './ProductCard';
import '../styles/AddedToCartModal.css';

const AddedToCartModal = () => {
  const {
    isAddedModalOpen,
    lastAddedProduct,
    closeAddedModal,
    openCart,
    cartItems,
  } = useCart();

  const [suggestedProducts, setSuggestedProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const carouselRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isAddedModalOpen) {
      loadSuggestedProducts();
    }
  }, [isAddedModalOpen]);

  const loadSuggestedProducts = async () => {
    try {
      setLoading(true);
      
      // Obtener productos promocionales
      const data = await apiClient.get('/ecommerce/product/promotional');
      
      if (Array.isArray(data)) {
        // Filtrar productos que ya están en el carrito
        const cartProductIds = cartItems.map(item => item.id);
        let filtered = data.filter(product => !cartProductIds.includes(product.id));
        
        // Filtrar productos con precio 0 si está configurado
        const hideZeroPriceProducts = process.env.REACT_APP_HIDE_ZERO_PRICE_PRODUCTS === 'true';
        if (hideZeroPriceProducts) {
          filtered = filtered.filter(product => product.precio && product.precio > 0);
        }
        
        // Tomar los primeros 6 productos sugeridos
        setSuggestedProducts(filtered.slice(0, 6));
      }
    } catch (err) {
      setSuggestedProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleContinueShopping = () => {
    closeAddedModal();
  };

  const handleGoToCart = () => {
    closeAddedModal();
    openCart();
  };

  const handleViewAllProducts = () => {
    closeAddedModal();
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleProductClick = (productId) => {
    // Función para forzar scroll al tope
    const forceScrollToTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    
    closeAddedModal();
    
    // Múltiples intentos de scroll inmediatos
    forceScrollToTop();
    
    // Navegar después de asegurar el scroll
    setTimeout(() => {
      forceScrollToTop();
      navigate(`/producto/${productId}`);
      
      // Intentos adicionales después de la navegación
      setTimeout(forceScrollToTop, 10);
      setTimeout(forceScrollToTop, 50);
      setTimeout(forceScrollToTop, 100);
    }, 50);
  };

  const scroll = (direction) => {
    if (carouselRef.current) {
      const scrollAmount = 280;
      const newScrollPosition = carouselRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      
      carouselRef.current.scrollTo({
        left: newScrollPosition,
        behavior: 'smooth'
      });
    }
  };

  const getProductImage = (product) => {
    if (product.imagenes && Array.isArray(product.imagenes) && product.imagenes.length > 0) {
      const validImage = product.imagenes.find(img => img && img.trim() !== '');
      if (validImage) return validImage;
    }
    if (product.imagen && product.imagen.trim() !== '') {
      return product.imagen;
    }
    return 'https://placehold.co/150x150?text=Sin+Imagen';
  };

  if (!isAddedModalOpen || !lastAddedProduct) return null;

  return (
    <>
      {/* Overlay oscuro */}
      <div className="added-modal-overlay" onClick={closeAddedModal}></div>
      
      {/* Modal */}
      <div className="added-modal">
        {/* Header con botón cerrar */}
        <button 
          className="added-modal-close" 
          onClick={closeAddedModal}
          aria-label="Cerrar"
        >
          ✕
        </button>

        {/* Contenido principal */}
        <div className="added-modal-content">
          {/* Confirmación de producto agregado */}
          <div className="added-confirmation">
            <div className="added-check-icon">✓</div>
            <h2 className="added-title">¡Agregado a tu carrito!</h2>
          </div>

          {/* Producto agregado */}
          <div className="added-product">
            <div className="added-product-image">
              <img 
                src={getProductImage(lastAddedProduct)} 
                alt={lastAddedProduct.descripcion}
                onError={(e) => {
                  e.target.src = 'https://placehold.co/150x150?text=Sin+Imagen';
                }}
              />
            </div>
            <div className="added-product-info">
              <h3 className="added-product-name">{lastAddedProduct.descripcion}</h3>
              <p className="added-product-price">
                ${lastAddedProduct.precio.toLocaleString()}
              </p>
              {lastAddedProduct.categoria && (
                <p className="added-product-category">{lastAddedProduct.categoria}</p>
              )}
            </div>
          </div>

          {/* Botones de acción principales */}
          <div className="added-actions">
            <button 
              className="btn btn-primary added-action-btn"
              onClick={handleContinueShopping}
            >
              Seguir comprando
            </button>
            <button 
              className="btn btn-whatsapp added-action-btn"
              onClick={handleGoToCart}
            >
              🛒 Ir al carrito
            </button>
          </div>

          {/* Productos sugeridos */}
          {suggestedProducts.length > 0 && (
            <div className="added-suggestions">
              <h3 className="added-suggestions-title">
                También te puede interesar
              </h3>
              
              <div className="added-carousel-container">
                {suggestedProducts.length > 2 && (
                  <button 
                    className="added-carousel-button added-carousel-button-left"
                    onClick={() => scroll('left')}
                    aria-label="Anterior"
                  >
                    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}

                <div className="added-carousel-track" ref={carouselRef}>
                  {suggestedProducts.map((product) => (
                    <div 
                      key={product.id} 
                      className="added-carousel-item"
                      onClick={() => handleProductClick(product.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <ProductCard product={product} showActions={false} />
                    </div>
                  ))}
                </div>

                {suggestedProducts.length > 2 && (
                  <button 
                    className="added-carousel-button added-carousel-button-right"
                    onClick={() => scroll('right')}
                    aria-label="Siguiente"
                  >
                    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                )}
              </div>

              <button 
                className="added-view-all-btn"
                onClick={handleViewAllProducts}
              >
                Ver más productos →
              </button>
            </div>
          )}

          {loading && (
            <div className="added-loading">
              <div className="spinner"></div>
              <p>Cargando sugerencias...</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AddedToCartModal;

